/**
 * AirGPU API Types
 * Type definitions for AirGPU API integration
 * API Docs: https://api.airgpu.com/docs
 */

// ============================================
// AIRGPU API REQUEST TYPES
// ============================================

/**
 * Machine creation configuration
 */
export interface CreateMachineConfig {
  name?: string; // Optional custom name (not stored by AirGPU)
  provider: AirGPUProvider; // Required. A machine created without it cannot be started.
  region: AirGPURegion;
  instance_type: AirGPUInstanceType;
  os: AirGPUOS;
  vcpus: number; // Required. Must match the instance type.
  ram: number; // GB. Required. Must match the instance type.
  provisioning_type?: AirGPUProvisioningType; // Default: on-demand
  root_volume_size?: AirGPURootVolumeSize; // GB, Default: 100
  max_upload?: AirGPUMaxUpload; // Mbit/s, Default: 15
}

/**
 * Machine start options
 */
export interface StartMachineOptions {
  provisioning_type?: AirGPUProvisioningType;
  /**
   * IP addresses or CIDR ranges permitted to reach the machine's streaming
   * ports. AirGPU opens those ports to EVERY address when this is omitted, so
   * callers must always supply it.
   */
  allowed_ips?: string[];
}

// ============================================
// AIRGPU API RESPONSE TYPES
// ============================================

/**
 * Machine response from AirGPU API
 */
export interface AirGPUMachine {
  id: string; // Machine ID (e.g., "m_xxxxxxxxxxxxxxxx")
  account: string; // Account ID
  created_at: number; // Unix timestamp (milliseconds)
  instance_type: AirGPUInstanceType;
  status: AirGPUMachineStatus;
  provider: 'aws';
  region: AirGPURegion;
  os: AirGPUOS;
  vcpus: number;
  ram: number; // GB
  root_volume_size: number; // GB
  max_upload: number; // Mbit/s
  public_ip: string | null; // Only available when running
  password: string | null; // Only available when running
  last_started_at: number | null; // Unix timestamp (milliseconds)
}

/**
 * Response from POST /machines (create)
 */
export interface CreateMachineResponse {
  id: string;
}

/**
 * Response from GET /machines/{id}
 */
export type GetMachineResponse = AirGPUMachine;

/**
 * Response from POST /machines/{id}/start
 */
export interface StartMachineResponse {
  success: boolean;
}

/**
 * Response from POST /machines/{id}/stop
 */
export interface StopMachineResponse {
  success: boolean;
}

/**
 * Response from DELETE /machines/{id}
 */
export interface DeleteMachineResponse {
  success: boolean;
}

/**
 * Error response from AirGPU API
 */
export interface AirGPUErrorResponse {
  /** Set by the AirGPU application itself, e.g. "machine is not off". */
  error?: string;
  /** Set by their API gateway, e.g. "Internal server error" on a 502. */
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
}

// ============================================
// AIRGPU ENUMS / CONSTANTS
// ============================================

/**
 * Machine status values from AirGPU API
 */
export type AirGPUMachineStatus =
  | 'off'
  | 'starting'
  | 'starting_pending' // Start requested, waiting for capacity
  | 'initializing'
  | 'running'
  | 'stopping'
  | 'backing_up';

/**
 * Instance types available on AirGPU
 *
 * Documented in the API reference (supplied as a PDF; there is no hosted spec,
 * and https://api.airgpu.com/docs returns 403):
 * - g4dn.xlarge:  NVIDIA Tesla T4 (16GB VRAM, 4 vCPU / 16GB RAM)
 * - g4dn.2xlarge: NVIDIA Tesla T4 (16GB VRAM, 8 vCPU / 32GB RAM)
 * - g5.2xlarge: NVIDIA A10G (24GB VRAM)
 *
 * Undocumented but confirmed available by the supplier (Sep 2026):
 * - g6.2xlarge / g6.4xlarge: NVIDIA L4 (24GB VRAM)
 *
 * Deliberately NOT sold:
 * - g6e.2xlarge / g6e.4xlarge (L40S). Still creatable via the API, but the
 *   supplier reports capacity problems in most locations and has pulled it
 *   from their own storefront. Creation is not validated upstream, so selling
 *   it means taking payment for a machine that may never start.
 * - g5.4xlarge is available and documented, but we do not list it yet.
 */
export type AirGPUInstanceType =
  | 'g4dn.xlarge'
  | 'g4dn.2xlarge'
  | 'g5.2xlarge'
  | 'g6.2xlarge'
  | 'g6.4xlarge';

/**
 * Infrastructure provider backing the machine.
 * Required on creation — AirGPU does not validate the create payload, and a
 * machine stored without a provider fails at start time with an opaque 502.
 */
export type AirGPUProvider = 'aws';

/**
 * Root volume sizes accepted by the API (GB).
 * Discrete values, not a range.
 */
export type AirGPURootVolumeSize = 100 | 150 | 200 | 250 | 300 | 400 | 500;

/**
 * Provisioning types
 * - on-demand: Always available, higher cost
 * - spot: Cheaper but can be interrupted (windows-server-2022 only)
 */
export type AirGPUProvisioningType = 'on-demand' | 'spot';

/**
 * Operating systems
 * - windows-server-2022: Standard Windows Server
 * - windows-10-byol: Windows 10 (Bring Your Own License)
 */
export type AirGPUOS = 'windows-server-2022' | 'windows-10-byol';

/**
 * Max upload bitrate options (Mbit/s)
 */
export type AirGPUMaxUpload = 7 | 15 | 25 | 45;

/**
 * Service regions we currently offer to customers.
 *
 * NOTE: this is deliberately a SUBSET. The upstream machine API supports more
 * regions than are listed here; we only sell the ones our infrastructure
 * agreements cover, so the extra upstream regions are intentionally absent.
 * Do not "restore" a region just because the upstream API accepts it.
 *
 * This union is the single source of truth for the offered catalogue: it is
 * also the validation set for new machines (via `AIRGPU_REGIONS` ->
 * `machineRegionSchema`) and the basis of the public region count.
 */
export type AirGPURegion =
  | 'us-east-1'        // N. Virginia
  | 'us-east-2'        // Ohio
  | 'us-west-1'        // N. California
  | 'us-west-2'        // Oregon
  | 'eu-west-1'        // Ireland
  | 'eu-west-2'        // London
  | 'eu-central-1'     // Frankfurt
  | 'eu-north-1'       // Stockholm
  | 'ap-northeast-1'   // Tokyo
  | 'ap-northeast-2'   // Seoul
  | 'ap-southeast-1'   // Singapore
  | 'ap-southeast-2'   // Sydney
  | 'ap-south-1'       // Mumbai
  | 'ap-east-1'        // Hong Kong
  | 'ca-central-1'     // Canada
  | 'af-south-1';      // Cape Town

// ============================================
// CONSTANTS
// ============================================

/**
 * Display names for the offered regions.
 *
 * Keep in sync with `AirGPURegion` above, and note the same caveat: the
 * upstream API exposes additional regions that we deliberately do not offer.
 * Anything derived from this map (region pickers, the marketing region count,
 * `machineRegionSchema`, `REGIONS` in `lib/catalog/filters.ts`) follows
 * automatically -- the hand-written grouping `REGION_OPTIONS` in
 * `lib/data/gpu-products.ts` (which the create form and the storefront filters
 * group by), the one in `app/(shop)/requirements/page.tsx` and the region list
 * in the `app/(shop)/faq/page.tsx` answer do not, so update those too.
 */
export const AIRGPU_REGIONS: Record<AirGPURegion, string> = {
  'us-east-1': 'US East (N. Virginia)',
  'us-east-2': 'US East (Ohio)',
  'us-west-1': 'US West (N. California)',
  'us-west-2': 'US West (Oregon)',
  'eu-west-1': 'Europe (Ireland)',
  'eu-west-2': 'Europe (London)',
  'eu-central-1': 'Europe (Frankfurt)',
  'eu-north-1': 'Europe (Stockholm)',
  'ap-northeast-1': 'Asia Pacific (Tokyo)',
  'ap-northeast-2': 'Asia Pacific (Seoul)',
  'ap-southeast-1': 'Asia Pacific (Singapore)',
  'ap-southeast-2': 'Asia Pacific (Sydney)',
  'ap-south-1': 'Asia Pacific (Mumbai)',
  'ap-east-1': 'Asia Pacific (Hong Kong)',
  'ca-central-1': 'Canada (Central)',
  'af-south-1': 'Africa (Cape Town)',
};

/**
 * Number of regions we offer -- this is the figure quoted publicly on the site.
 * Derived from AIRGPU_REGIONS so the marketing copy cannot drift from what the
 * machine form will actually accept. It is not the upstream API's region count.
 */
export const AIRGPU_REGION_COUNT = Object.keys(AIRGPU_REGIONS).length;

/**
 * Instance types with specs and pricing
 */
export const AIRGPU_INSTANCE_TYPES: Record<AirGPUInstanceType, {
  name: string;
  gpu: string;
  vram: number; // GB
  vcpus: number;
  ram: number; // GB
  description: string;
}> = {
  'g4dn.xlarge': {
    // Named apart from the 2xlarge so the create form, review step and machine
    // cards - which show only the name - can tell the two T4 sizes apart.
    name: 'Tesla T4 Lite',
    gpu: 'NVIDIA Tesla T4',
    vram: 16,
    vcpus: 4,
    ram: 16,
    description: 'Lowest-cost way onto the fleet for lighter sessions and testing',
  },
  'g4dn.2xlarge': {
    name: 'Tesla T4',
    gpu: 'NVIDIA Tesla T4',
    vram: 16,
    vcpus: 8,
    ram: 32,
    description: 'Great for gaming, streaming, and light ML workloads',
  },
  'g5.2xlarge': {
    name: 'A10G',
    gpu: 'NVIDIA A10G',
    vram: 24,
    vcpus: 8,
    ram: 32,
    description: 'Powerful GPU for gaming at high settings and ML training',
  },
  'g6.2xlarge': {
    name: 'L4',
    gpu: 'NVIDIA L4',
    vram: 24,
    vcpus: 8,
    ram: 32,
    description: 'Next-gen efficiency for gaming, content creation, and AI inference',
  },
  'g6.4xlarge': {
    // "Pro" for the same reason as the T4 Lite: the form, review step and
    // machine cards show only the name, and two sizes both called "L4" read as
    // one product at two prices.
    name: 'L4 Pro',
    gpu: 'NVIDIA L4',
    vram: 24,
    vcpus: 16,
    ram: 64,
    description: 'Next-gen efficiency with more CPU and RAM for demanding workloads',
  },
};

/**
 * GPU availability by region
 *
 * Sources:
 * - T4 & A10G: AirGPU API reference (PDF, Sep 2026)
 * - L4: AirGPU storefront, availability confirmed by the supplier (Sep 2026).
 *   They gave no per-region breakdown, so this list is unverified.
 *
 * T4 (g4dn.xlarge/g4dn.2xlarge): Available in every region we offer
 * A10G (g5.2xlarge): Available in 12 regions per API docs
 * L4 (g6.2xlarge/g6.4xlarge): Available in 10 regions per supplier website
 */
export const GPU_REGION_AVAILABILITY: Record<AirGPUInstanceType, AirGPURegion[]> = {
  // Tesla T4 - available in every region we offer (the one exclusion in the
  // API reference, ap-southeast-3, is not in our list).
  'g4dn.xlarge': [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1', 'eu-north-1',
    'ap-northeast-1', 'ap-northeast-2', 'ap-southeast-1', 'ap-southeast-2', 'ap-south-1', 'ap-east-1',
    'ca-central-1', 'af-south-1',
  ],
  'g4dn.2xlarge': [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1', 'eu-north-1',
    'ap-northeast-1', 'ap-northeast-2', 'ap-southeast-1', 'ap-southeast-2', 'ap-south-1', 'ap-east-1',
    'ca-central-1', 'af-south-1',
  ],
  // A10G - Available in 12 regions (per API docs)
  'g5.2xlarge': [
    'us-east-1', 'us-east-2', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1', 'eu-north-1',
    'ap-northeast-1', 'ap-northeast-2', 'ap-southeast-2', 'ap-south-1',
    'ca-central-1',
  ],
  // L4 - 10 regions per the supplier's storefront (unverified; see above)
  'g6.2xlarge': [
    'us-east-1', 'us-east-2', 'us-west-2',
    'eu-central-1', 'eu-west-2', 'eu-north-1',
    'ap-northeast-1', 'ap-southeast-2', 'ap-south-1',
    'ca-central-1',
  ],
  'g6.4xlarge': [
    'us-east-1', 'us-east-2', 'us-west-2',
    'eu-central-1', 'eu-west-2', 'eu-north-1',
    'ap-northeast-1', 'ap-southeast-2', 'ap-south-1',
    'ca-central-1',
  ],
};

/**
 * Get available regions for a given instance type
 */
export function getAvailableRegionsForInstance(instanceType: AirGPUInstanceType): AirGPURegion[] {
  return GPU_REGION_AVAILABILITY[instanceType];
}

/**
 * Get available instance types for a given region
 */
export function getAvailableInstancesForRegion(region: AirGPURegion): AirGPUInstanceType[] {
  return (Object.keys(GPU_REGION_AVAILABILITY) as AirGPUInstanceType[]).filter(
    (instanceType) => GPU_REGION_AVAILABILITY[instanceType].includes(region)
  );
}

/**
 * Check if an instance type is available in a region
 */
export function isInstanceAvailableInRegion(instanceType: AirGPUInstanceType, region: AirGPURegion): boolean {
  return Object.hasOwn(GPU_REGION_AVAILABILITY, instanceType)
    && GPU_REGION_AVAILABILITY[instanceType].includes(region);
}

/**
 * OS options with details
 */
export const AIRGPU_OS_OPTIONS: Record<AirGPUOS, {
  name: string;
  description: string;
  spotAvailable: boolean;
}> = {
  'windows-server-2022': {
    name: 'Windows Server 2022',
    description: 'Standard Windows Server environment',
    spotAvailable: true,
  },
  'windows-10-byol': {
    name: 'Windows 10 (BYOL)',
    description: 'Windows 10 - Bring Your Own License',
    spotAvailable: false,
  },
};

/**
 * Max upload options with pricing impact
 */
export const AIRGPU_MAX_UPLOAD_OPTIONS: AirGPUMaxUpload[] = [7, 15, 25, 45];

/**
 * Volume size constraints
 */
export const AIRGPU_VOLUME_SIZE = {
  min: 100,
  max: 500,
  default: 100,
};

// ============================================
// TYPE GUARDS
// ============================================

export function isValidRegion(region: string): region is AirGPURegion {
  return Object.hasOwn(AIRGPU_REGIONS, region);
}

export function isValidInstanceType(type: string): type is AirGPUInstanceType {
  return Object.hasOwn(AIRGPU_INSTANCE_TYPES, type);
}

export function isValidOS(os: string): os is AirGPUOS {
  return Object.hasOwn(AIRGPU_OS_OPTIONS, os);
}

export function isValidProvisioningType(type: string): type is AirGPUProvisioningType {
  return type === 'on-demand' || type === 'spot';
}

export function isValidMaxUpload(value: number): value is AirGPUMaxUpload {
  return AIRGPU_MAX_UPLOAD_OPTIONS.includes(value as AirGPUMaxUpload);
}

export function isValidVolumeSize(size: number): boolean {
  return size >= AIRGPU_VOLUME_SIZE.min && size <= AIRGPU_VOLUME_SIZE.max;
}

/**
 * Validates that spot provisioning is only used with compatible OS
 */
export function isValidSpotConfiguration(provisioningType: AirGPUProvisioningType, os: AirGPUOS): boolean {
  if (provisioningType === 'spot') {
    return AIRGPU_OS_OPTIONS[os].spotAvailable;
  }
  return true;
}
